import pick from 'lodash/pick';
import range from 'lodash/range';
import {Component} from 'panel';

import {Section, SectionItem} from 'common/components/items-menu/types';
import {defineAndMakeJsxFactory} from 'common/util/jsx';

import {template} from './template';
import css from './index.styl?inline';

/** a 1-indexed step-to-step selection */
export interface RangeSelection {
  /** true implies lack of from/to */
  isEntireRange: boolean;
  /** 1-indexed start and end */
  range: {
    from?: number;
    to?: number;
  };
}

export type SelectRangeEvent = CustomEvent<RangeSelection>;

/** widget for selecting a range of two adjacent steps, or all steps. */
export class StepRangeSelector extends Component<UnknownObj> {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      selected: {type: `json`, default: null},
      'num-steps': {type: `number`, default: 0},
      label: {type: `string`, default: null, description: `Optional label before the menu.`},
      theme: {type: `string`, default: `secondary`, enum: [`builder`, `secondary`, `select`]},
      tooltip: `string`,
      'disable-all-steps': `boolean`,
      disabled: `boolean`,
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {
        contextPanelTransform: null,
      },
      helpers: {
        getOptions: (): Section[] => [{items: this.generateRanges()}],
        onChange: (ev) => {
          const item = pick(ev.detail.item.value, [`isEntireRange`, `range`]);
          this.dispatchEvent(new CustomEvent(`select`, {detail: item}));
        },
        contextPanelTransform: () => {
          return this.attr(`tooltip-data`);
        },
      },
    };
  }

  private generateRanges(): Array<SectionItem & {value: RangeSelection}> {
    const entry = this.attr(`selected`);
    let hasMatch = false;
    const steps = this.attr(`num-steps`);
    const ranges = steps ? range(1, steps) : [];
    const {from: eventFrom, to: eventTo} = entry.range;

    const allEventsItem: SectionItem & {value: RangeSelection} = {
      isSelected: entry.isEntireRange,
      label: `All steps`,
      value: {isEntireRange: true, range: {}},
    };

    if (this.attr(`disable-all-steps`)) {
      allEventsItem.isDisabled = true;
      allEventsItem.tooltip = {text: `All steps is not supported for this chart`};
    }

    const rangeItems = ranges.map((i) => {
      const _range = {from: i, to: i + 1};
      const isSelected = eventFrom === _range.from && eventTo === _range.to;
      hasMatch = hasMatch || isSelected;

      return {
        isSelected,
        label: `Step ${_range.from} \u2192 Step ${_range.to}`,
        value: {isEntireRange: false, range: _range},
      };
    });

    // if event has no match, it means a multi step
    // range was previously selected
    if (!entry.isEntireRange && !hasMatch) {
      rangeItems.push({
        isSelected: true,
        label: `Step ${eventFrom} \u2192 Step ${eventTo}`,
        value: {isEntireRange: false, range: entry.range},
      });
    }

    return [allEventsItem, ...rangeItems];
  }
}

export const StepRangeSelectorEl = defineAndMakeJsxFactory(`mp-step-range-selector`, StepRangeSelector);
